import React, { memo, useCallback, useState } from "react";
import { Trash2, RefreshCw, Loader2, Info } from "lucide-react";
import { storageApi } from "../lib/api";
import { formatBytesTR, formatDateTimeTR } from "../lib/storageFormat";

// FAZ B — Depolama Temizlik Raporu. SADECE görüntüleme; hiçbir dosya silinmez.
// Satırlar React.memo ile izole edilir (liste uzun olabilir).
const OrphanRow = memo(({ item }) => (
  <div
    className="flex items-center gap-2 px-2 py-1 rounded border border-white/10 bg-sertex-surface/40"
    data-testid={`orphan-row-${item.path}`}
  >
    <Trash2 className="h-3.5 w-3.5 text-amber-300/80 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-[12px] font-mono text-sertex-text truncate">{item.filename}</div>
      <div className="text-[10px] text-sertex-textMuted truncate">
        {item.uploaded_at ? formatDateTimeTR(item.uploaded_at) : "yükleme tarihi bilinmiyor"}
        {item.deleted_task_id ? ` · silinen görev: ${item.deleted_task_id.slice(0, 8)}` : ""}
      </div>
    </div>
    <span className="text-[11px] tabular-nums text-sertex-textSecondary shrink-0">
      {formatBytesTR(item.size)}
    </span>
  </div>
));
OrphanRow.displayName = "OrphanRow";

export const StorageCleanupReport = () => {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (refresh) => {
    setLoading(true);
    setError("");
    try {
      const [rep, use] = await Promise.all([
        storageApi.orphans(refresh, 50),
        storageApi.usage(refresh),
      ]);
      setData(rep);
      setUsage(use);
    } catch (e) {
      setError(e?.response?.data?.detail || "Rapor alınamadı");
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div
      className="rounded-lg border border-white/10 bg-sertex-surface/60 p-4 space-y-3"
      data-testid="storage-cleanup-report"
    >
      <div className="flex items-center gap-2">
        <Trash2 className="h-4 w-4 text-amber-300" />
        <span className="text-sertex-text font-semibold">Depolama Temizlik Raporu</span>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          data-testid="orphan-report-refresh"
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/40 text-[11px] transition-colors disabled:opacity-40"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Raporu Yenile
        </button>
      </div>

      {!data && !loading && !error && (
        <div className="text-[12px] text-sertex-textMuted" data-testid="orphan-report-idle">
          Tarama pahalı olduğu için otomatik başlatılmaz. "Raporu Yenile" ile hesaplayın.
        </div>
      )}

      {error && (
        <div className="text-[12px] text-rose-300" data-testid="orphan-report-error">{error}</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-2 text-[12px]" data-testid="orphan-report-stats">
            <div className="rounded border border-white/10 px-2 py-1.5">
              <div className="text-sertex-textMuted text-[10px]">AKTİF DOSYALAR</div>
              <div className="text-sertex-text tabular-nums">
                {usage?.active_files ?? "—"} dosya · {formatBytesTR(usage?.active_bytes || 0)}
              </div>
            </div>
            <div className="rounded border border-white/10 px-2 py-1.5">
              <div className="text-sertex-textMuted text-[10px]">YETİM DOSYALAR</div>
              <div className="text-amber-200 tabular-nums">
                {data.orphan_files} dosya · {formatBytesTR(data.orphan_bytes)}
              </div>
            </div>
            <div className="rounded border border-white/10 px-2 py-1.5">
              <div className="text-sertex-textMuted text-[10px]">TEKİLLEŞTİRME KAZANCI</div>
              <div className="text-emerald-300 tabular-nums">
                {formatBytesTR(usage?.dedup_saved_bytes || 0)}
              </div>
            </div>
            <div className="rounded border border-white/10 px-2 py-1.5">
              <div className="text-sertex-textMuted text-[10px]">KOTA DOLULUĞU</div>
              <div className="text-sertex-text tabular-nums">
                {usage ? `%${usage.used_percent}` : "—"} / {formatBytesTR(usage?.quota_bytes || 0)}
              </div>
            </div>
          </div>

          <div className="text-[11px] text-sertex-textMuted">
            En büyük {Math.min(50, data.items.length)} yetim dosya listeleniyor
            {data.orphan_files > data.items.length ? ` (toplam ${data.orphan_files})` : ""}.
          </div>

          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="orphan-report-list">
            {data.items.length === 0 ? (
              <div className="text-[12px] text-emerald-300" data-testid="orphan-report-empty">
                Yetim dosya yok — depolama temiz.
              </div>
            ) : (
              data.items.map((it) => <OrphanRow key={it.path} item={it} />)
            )}
          </div>

          <p className="flex gap-2 text-[11px] text-sertex-textMuted leading-relaxed">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-sertex-cyan/70" />
            <span>
              Depolama sağlayıcısı dosya silmeyi desteklemediği için bu dosyalar şimdilik
              silinemiyor. İleride sağlayıcı geçişi yapılırsa temizlenecek. Bu yüzden burada
              "Sil" düğmesi bulunmuyor.
            </span>
          </p>

          <div className="text-[10px] text-sertex-textMuted" data-testid="orphan-report-meta">
            Son hesaplama: {formatDateTimeTR(data.computed_at)} · Yöntem:{" "}
            {data.method === "storage_list" ? "dosya deposu sayımı" : "veritabanı kayıtları"}
          </div>
        </>
      )}
    </div>
  );
};

export default StorageCleanupReport;
